import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { UserListResponse } from '@utils/types/users/user.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export const useGetUsers = () => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const qString = (searchParams.q as string | undefined) || undefined;

  const fetchUsers = async () => {
    const { users: usersUrl } = GET_URL_NEW({
      offset,
      limit,
      q: qString,
      sort: sortField,
      direction: sortDirection
    });
    const { data } = await Axios.get<UserListResponse>(usersUrl);

    return data;
  };

  return useQuery<UserListResponse, AxiosError<BaseError>>(
    ['users', offset, limit, qString, sortField, sortDirection],
    fetchUsers,
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,

      keepPreviousData: true,

      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

      retry: (failureCount, error) => {
        // Don't retry on 403 (forbidden - user doesn't have permission)
        if (error?.response?.status === 403) {
          return false;
        }
        // Default retry logic (3 times)
        return failureCount < 3;
      },

      onError: error => {
        // Don't show error toast for 403 - let the component handle it
        if (error.response?.status === 403) {
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = error.response?.data as any;
        const msg =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          t('an-error-has-occurred');
        toast.error(String(msg));
      }
    }
  );
};
